import { call, put, takeLatest, select, delay } from 'redux-saga/effects';

import {
  demographicsFilterRequest,
  fetchAnalyticsCampaignAsync,
  fetchAnalyticsSponsorshipAsync,
  fetchAnalyticsSponsorshipSetAsync,
  fetchHeatMapAsync,
  fetchFilteredAcceptanceOverTimeAsync,
  fetchFilteredDemographicsAsync,
  fetchFilteredResponsesAsync,
  fetchFilteredShortAnswerResponsesAsync,
  fetchFilteredTotalsAsync,
  overviewFilterRequest,
  getCSVDataAsync,
  updateDownloadPercentage,
  toggleIsLoadingCSVData,
  setCSVDataDefault,
} from './actions';

import { isLoadingCSVDataSelector } from './selectors';

import { container } from '../../container';

import {
  processAnalyticsDemographics,
  processFilteringBodyParams,
  processReceivedResponses,
  processReceivedShortAnswers,
  transformAcceptanceData,
  transformSeries,
} from '../../utils/receivedData';

import { setDemographicCsvDataType } from '../../utils/reporting';
import { brandInfoSelector } from '../brand';
import { formatTimezonedDateToUTC } from '../../utils/date/dateHelpers';

const analyticsService = container.get('AnalyticsService');

function* processOverviewFilterRequest({ payload }) {
  try {
    const { campaignId, adSetId, adId, type } = payload;
    const { selectedGender, selectedDevice, selectedCity, ageFrom, ageTo, fromDate, toDate } =
      payload.query;
    const brandInfo = yield select(brandInfoSelector);

    const bodyParams = processFilteringBodyParams({
      campaignId,
      adSetId,
      adId,
      type,
      gender: selectedGender,
      platform: selectedDevice,
      city: selectedCity,
      fromAge: ageFrom,
      toAge: ageTo,
      fromDate: formatTimezonedDateToUTC(fromDate, brandInfo.timezoneId),
      toDate: formatTimezonedDateToUTC(toDate, brandInfo.timezoneId),
      brandId: brandInfo?.brandIdFromAdmin,
    });

    yield put(fetchFilteredAcceptanceOverTimeAsync.request(bodyParams));
    yield put(fetchFilteredDemographicsAsync.request(bodyParams));
    yield put(fetchFilteredResponsesAsync.request(bodyParams));
    yield put(fetchFilteredShortAnswerResponsesAsync.request(bodyParams));
    yield put(fetchFilteredTotalsAsync.request(bodyParams));
    yield put(fetchHeatMapAsync.request(bodyParams));
  } catch (error) {
    console.log(error);
  }
}

function* processDemographicsFilterRequest({ payload }) {
  try {
    const { campaignId, adSetId, adId, type } = payload;
    const { gender, platform, city, fromAge, toAge, fromDate, toDate, activity, responseFilter } =
      payload.query;
    const brandInfo = yield select(brandInfoSelector);

    const bodyParams = processFilteringBodyParams({
      campaignId,
      adSetId,
      adId,
      type,
      gender,
      platform,
      city,
      fromAge,
      toAge,
      fromDate: formatTimezonedDateToUTC(fromDate, brandInfo.timezoneId),
      toDate: formatTimezonedDateToUTC(toDate, brandInfo.timezoneId),
      activity,
      responseFilter,
      brandId: brandInfo?.brandIdFromAdmin,
    });

    yield put(fetchFilteredAcceptanceOverTimeAsync.request(bodyParams));
    yield put(fetchFilteredDemographicsAsync.request(bodyParams));
    yield put(fetchFilteredResponsesAsync.request(bodyParams));
    yield put(fetchFilteredShortAnswerResponsesAsync.request(bodyParams));
    yield put(fetchFilteredTotalsAsync.request(bodyParams));
  } catch (error) {
    console.log(error);
  }
}

function* fetchFilteredAcceptanceOverTime({ payload }) {
  try {
    const response = yield call(analyticsService.fetchAcceptanceOverTime, {
      campaignId: payload.campaignId,
      adSetId: payload.adSetId,
      adId: payload.adId,
      type: payload.type,
      gender: payload.gender,
      platform: payload.platform,
      city: payload.city,
      fromAge: payload.fromAge,
      toAge: payload.toAge,
      fromDate: payload.fromDate,
      toDate: payload.toDate,
      brandId: payload.brandId,
    });

    const series = transformAcceptanceData(response);

    yield put(fetchFilteredAcceptanceOverTimeAsync.success(series));
  } catch (error) {
    yield put(fetchFilteredAcceptanceOverTimeAsync.failure(error));
  }
}

function* fetchFilteredDemographics({ payload }) {
  try {
    const response = yield call(analyticsService.fetchDemographics, {
      campaignId: payload.campaignId,
      adSetId: payload.adSetId,
      adId: payload.adId,
      type: payload.type,
      gender: payload.gender,
      platform: payload.platform,
      city: payload.city,
      fromAge: payload.fromAge,
      toAge: payload.toAge,
      fromDate: payload.fromDate,
      toDate: payload.toDate,
      activity: payload.activity,
      responseFilter: payload.responseFilter,
      brandId: payload.brandId,
    });

    const transformedData = processAnalyticsDemographics(response);

    yield put(fetchFilteredDemographicsAsync.success(transformedData));
  } catch (error) {
    yield put(fetchFilteredDemographicsAsync.failure(error));
  }
}

function* fetchFilteredResponses({ payload }) {
  try {
    const response = yield call(analyticsService.fetchResponses, {
      campaignId: payload.campaignId,
      adSetId: payload.adSetId,
      adId: payload.adId,
      type: payload.type,
      gender: payload.gender,
      platform: payload.platform,
      city: payload.city,
      fromAge: payload.fromAge,
      toAge: payload.toAge,
      fromDate: payload.fromDate,
      toDate: payload.toDate,
      brandId: payload.brandId,
    });

    const transformedData = processReceivedResponses(response);

    yield put(fetchFilteredResponsesAsync.success(transformedData));
  } catch (error) {
    yield put(fetchFilteredResponsesAsync.failure(error));
  }
}

function* fetchFilteredShortAnswersResponses({ payload }) {
  try {
    const response = yield call(analyticsService.fetchShortAnswerResponses, {
      campaignId: payload.campaignId,
      adSetId: payload.adSetId,
      adId: payload.adId,
      type: payload.type,
      gender: payload.gender,
      platform: payload.platform,
      city: payload.city,
      fromAge: payload.fromAge,
      toAge: payload.toAge,
      fromDate: payload.fromDate,
      toDate: payload.toDate,
      brandId: payload.brandId,
    });

    const transformedData = processReceivedShortAnswers(response);

    yield put(fetchFilteredShortAnswerResponsesAsync.success(transformedData));
  } catch (error) {
    yield put(fetchFilteredShortAnswerResponsesAsync.failure(error));
  }
}

function* fetchFilteredTotals({ payload }) {
  try {
    const response = yield call(analyticsService.fetchTotals, {
      campaignId: payload.campaignId,
      adSetId: payload.adSetId,
      adId: payload.adId,
      type: payload.type,
      gender: payload.gender,
      platform: payload.platform,
      city: payload.city,
      fromAge: payload.fromAge,
      toAge: payload.toAge,
      fromDate: payload.fromDate,
      toDate: payload.toDate,
      brandId: payload.brandId,
    });

    yield put(fetchFilteredTotalsAsync.success(response));
  } catch (error) {
    yield put(fetchFilteredTotalsAsync.failure(error));
  }
}

function* fetchHeatMap({ payload }) {
  try {
    const response = yield call(analyticsService.fetchHeatMap, payload);
    const { heatMapByInfluencerCity } = response;
    const transformedData = transformSeries(
      heatMapByInfluencerCity ? heatMapByInfluencerCity[0] : {}
    );

    yield put(fetchHeatMapAsync.success(transformedData));
  } catch (err) {
    yield put(fetchHeatMapAsync.failure(err));
  }
}

function* fetchAnalyticsCampaign({ payload }) {
  try {
    const { campaignId } = payload;
    const response = yield call(analyticsService.fetchAnalyticsCampaign, campaignId);

    yield put(fetchAnalyticsCampaignAsync.success(response));
  } catch (err) {
    yield put(fetchAnalyticsCampaignAsync.failure(err));
  }
}

function* fetchAnalyticsSponsorship({ payload }) {
  try {
    const { adId } = payload;
    const response = yield call(analyticsService.fetchAnalyticsSponsorship, adId);

    yield put(fetchAnalyticsSponsorshipAsync.success(response));
  } catch (err) {
    yield put(fetchAnalyticsSponsorshipAsync.failure(err));
  }
}

function* fetchAnalyticsSponsorshipSet({ payload }) {
  try {
    const { adSetId } = payload;
    let { tiomezoneId } = yield select(brandInfoSelector);

    const response = yield call(analyticsService.fetchAnalyticsSponsorshipSet, {
      adSetId,
      utcString: tiomezoneId,
    });

    // const { startDate, endDate } = response;

    yield put(fetchAnalyticsSponsorshipSetAsync.success(response));

    // yield put(
    //   setOverviewFilterOption({
    //     name: 'dateFrom',
    //     value: startDate,
    //   })
    // );

    // yield put(
    //   setOverviewFilterOption({
    //     name: 'dateTo',
    //     value: endDate,
    //   })
    // );

    // yield put(
    //   setDemographicsFilterOption({
    //     name: 'demographicsDateFrom',
    //     value: startDate,
    //   })
    // );

    // yield put(
    //   setDemographicsFilterOption({
    //     name: 'demographicsDateTo',
    //     value: endDate,
    //   })
    // );
  } catch (err) {
    yield put(fetchAnalyticsSponsorshipSetAsync.failure(err));
  }
}

function* getCSVDataSaga({ payload }) {
  try {
    const { csvDataType, currentTab, brandId, campaignId, adSetId, adId, type } = payload;
    const { questionId } = payload;

    const { selectedGender, selectedDevice, selectedCity, ageFrom, ageTo } = payload.filters;

    const { gender, platform, city, fromAge, toAge, fromDate, toDate, activity } = payload.filters;

    const additionalData = brandId ? { brandId } : {};
    const brandInfo = yield select(brandInfoSelector);

    const bodyParams = processFilteringBodyParams({
      campaignId,
      adSetId,
      adId,
      type,
      gender: currentTab === 'demographicTab' ? gender : selectedGender,
      platform: currentTab === 'demographicTab' ? platform : selectedDevice,
      city: currentTab === 'demographicTab' ? city : selectedCity,
      fromAge: currentTab === 'demographicTab' ? fromAge : ageFrom,
      toAge: currentTab === 'demographicTab' ? toAge : ageTo,
      fromDate,
      toDate,
    });

    let queryParams = {
      campaignId: bodyParams.campaignId,
      adSetId: bodyParams.adSetId,
      adId: bodyParams.adId,
      type: bodyParams.type,
      gender: bodyParams.gender,
      platform: bodyParams.platform,
      city: bodyParams.city,
      fromAge: bodyParams.fromAge,
      toAge: bodyParams.toAge,
      fromDate: formatTimezonedDateToUTC(fromDate, brandInfo.timezoneId),
      toDate: formatTimezonedDateToUTC(toDate, brandInfo.timezoneId),
      paginationId: '',
      csvDataType:
        currentTab === 'demographicTab' ? setDemographicCsvDataType(activity?.value) : csvDataType,
      ...additionalData,
    };

    if (questionId) queryParams = { ...queryParams, questionId };

    let response;

    if (csvDataType === 'ShortAnswer')
      response = yield call(analyticsService.fetchAnsweredQuestionDataForCSV, queryParams);
    else response = yield call(analyticsService.fetchDataForCSV, queryParams);

    const pages = response.Pages;
    let combineData = response.Data;

    // Removing some attributes from each row
    const Data = response.Data.map((row) => {
      const { InfluencerId, DeviceId, AdvertiserId, ...data } = row;
      return data;
    });

    combineData = Data;

    const isLoadingCSVData = yield select(isLoadingCSVDataSelector);

    if (pages > 1) {
      for (let i = 2; i <= pages; i++) {
        const isLoadingCSVDataInCircle = yield select(isLoadingCSVDataSelector);

        if (!isLoadingCSVDataInCircle) {
          combineData = [];
          yield put(setCSVDataDefault());
          break;
        }

        queryParams = {
          ...queryParams,
          paginationId: response.PaginationId,
        };

        if (csvDataType === 'ShortAnswer')
          response = yield call(analyticsService.fetchAnsweredQuestionDataForCSV, queryParams);
        else response = yield call(analyticsService.fetchDataForCSV, queryParams);

        // Removing some attributes from each row
        const Data = response.Data.map((row) => {
          const { InfluencerId, DeviceId, AdvertiserId, ...data } = row;
          return data;
        });

        combineData = [...combineData, ...Data];

        yield put(updateDownloadPercentage(((i / pages) * 100).toFixed()));
      }
      yield delay(1000);
    } else {
      yield put(updateDownloadPercentage(100));
      yield delay(1000);
    }

    if (!isLoadingCSVData) {
      combineData = [];
      yield put(setCSVDataDefault());
    }

    yield put(getCSVDataAsync.success(combineData));
    yield put(toggleIsLoadingCSVData(false));
    yield put(updateDownloadPercentage(0));
  } catch (err) {
    yield put(getCSVDataAsync.failure(err));
    yield put(toggleIsLoadingCSVData(false));
    yield put(updateDownloadPercentage(0));
    yield put(setCSVDataDefault());
  }
}

export function* analyticsActionWatcher() {
  yield takeLatest(demographicsFilterRequest, processDemographicsFilterRequest);
  yield takeLatest(overviewFilterRequest, processOverviewFilterRequest);
  yield takeLatest(fetchFilteredDemographicsAsync.request.type, fetchFilteredDemographics);
  yield takeLatest(fetchFilteredResponsesAsync.request.type, fetchFilteredResponses);
  yield takeLatest(
    fetchFilteredAcceptanceOverTimeAsync.request.type,
    fetchFilteredAcceptanceOverTime
  );
  yield takeLatest(
    fetchFilteredShortAnswerResponsesAsync.request.type,
    fetchFilteredShortAnswersResponses
  );
  yield takeLatest(fetchFilteredTotalsAsync.request.type, fetchFilteredTotals);
  yield takeLatest(fetchAnalyticsCampaignAsync.request.type, fetchAnalyticsCampaign);
  yield takeLatest(fetchAnalyticsSponsorshipAsync.request.type, fetchAnalyticsSponsorship);
  yield takeLatest(fetchAnalyticsSponsorshipSetAsync.request.type, fetchAnalyticsSponsorshipSet);
  yield takeLatest(fetchHeatMapAsync.request.type, fetchHeatMap);
  yield takeLatest(getCSVDataAsync.request.type, getCSVDataSaga);
}
